import { API_BASE } from '../../config.js';
import { handToPrint } from '../../features/print/handoff.js';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../i18n/formatters';
import usePrintables from '../../services/usePrintables';
import { useWallet } from '../../contexts/WalletContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import CategoryTree from './CategoryTree';
import CurriculumTree from './CurriculumTree';
import PrintableCard from './PrintableCard';
import PrintableDetail from './PrintableDetail';
import PrintablesLanding from './PrintablesLanding';
import PrintablesBreadcrumb from './PrintablesBreadcrumb';
import PurchaseSuccessModal from './PurchaseSuccessModal';
import CreditPurchaseModal from '../wallet/CreditPurchaseModal';
import { purchaseAndSave } from './purchaseFlow';
import { unsuppressPrintableRestore } from '../../services/printablesRestoreLedger';
import { categoryLabel } from './categoryLabel';
import useCloudLibrary from '../../hooks/useCloudLibrary';
import useCatalogContext, { useCurriculumJurisdictions, groupJurisdictions, LANGUAGE_FAMILY_LABELS, LANGUAGE_ALL } from '../../services/useCatalogContext';

const PRINTABLES_FEATURE_KEY = 'printables_purchase';
// Brightivity: the printables fork behind the front door (brightivity.dev/printables/<x> = /api/<x>).
const PRINTABLES_API = `${API_BASE}/printables`;

const GRADE_BANDS = [
  { key: 'all', min: null, max: null },
  { key: 'k2', min: 0, max: 2 },
  { key: 'g35', min: 3, max: 5 },
  { key: 'g68', min: 6, max: 8 },
  { key: 'g912', min: 9, max: 12 },
];

// Client-generated purchaseFlow error codes → catalog keys, translated at the
// display point only. Unknown/absent code renders the stored message as-is.
const PURCHASE_ERROR_KEYS = {
  purchaseFailed: 'purchase.errors.purchaseFailed',
  missingPayload: 'purchase.errors.missingPayload',
  prepareFailed: 'purchase.errors.prepareFailed',
  saveFailed: 'purchase.errors.saveFailed',
  saveThrew: 'purchase.errors.saveThrew',
};

export default function Printables() {
  const { t } = useTranslation('printables');
  const {
    posters, total, totalPages, loading, error,
    fetchRootCategories, fetchCategoryChildren, fetchCategory,
    fetchPosters, searchPosters, fetchPoster, fetchFeaturedPosters, fetchSimilarPosters,
    purchasePrintable, logSearch, fetchCatalogFacets,
    fetchCurriculumTree, fetchCurriculumChildren, fetchCurriculumNode,
  } = usePrintables();
  const { isAuthenticated, user } = useAuth();
  const { balance, getCost, canAfford, refreshBalance } = useWallet();
  const { showError } = useToast();
  const navigate = useNavigate();
  // Web library save target (URL-direct; the worker fetches server-side).
  const { saveDesign } = useCloudLibrary();
  // schoolStateCode keeps its old name: it still feeds the alignment-chip fetch
  // and the card's viewerStateCode, which are unrelated to the catalog filter.
  const { jurisdiction: schoolStateCode, jurisdictions, uiLanguageFamily } = useCatalogContext();
  // Parameterless, session-constant — see the note in useCatalogContext.
  const curriculumJurisdictions = useCurriculumJurisdictions();

  // IIG Phase 4 — batch alignment for the visible grid (be_printables). Fetched
  // directly (mirrors usePrintables' authed-fetch idiom) so the chip degrades
  // invisibly to {} if the endpoint is unreachable.
  const [alignments, setAlignments] = useState({});

  // Tier A/B (and master admins) get printables free — server enforces the same
  // rule in the purchase endpoint; this only drives the displayed price.
  const isFreeTier = !!(user?.masterAdmin || user?.tier === 'A' || user?.tier === 'B');
  const purchaseCost = isFreeTier ? 0 : (getCost(PRINTABLES_FEATURE_KEY) || null);

  const [purchaseInFlight, setPurchaseInFlight] = useState(false);
  const [successPrintable, setSuccessPrintable] = useState(null); // { printable, alreadyOwned, newBalance }
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  // Track posters the user owns locally (optimistic UI so "Buy" becomes "Print" immediately
  // without waiting for a refetch).
  const [locallyOwnedIds, setLocallyOwnedIds] = useState(new Set());

  // Restore state from sessionStorage on mount
  const savedState = (() => {
    try {
      const raw = sessionStorage.getItem('printables_view_state');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [showLanding, setShowLanding] = useState(savedState ? savedState.showLanding : true);
  const [selectedCategoryId, setSelectedCategoryId] = useState(savedState?.selectedCategoryId ?? null);
  // The curriculum tree's selection. A curriculum node is NOT a category — it
  // narrows the grid through poster_standards, not the nested-set subtree — so
  // it gets its own state and its own param. The two are mutually exclusive in
  // the client (each select handler clears the other); the server would happily
  // AND them, but nothing here ever sends both.
  const [selectedCurriculumNodeId, setSelectedCurriculumNodeId] = useState(savedState?.selectedCurriculumNodeId ?? null);
  const [curriculumNodeInfo, setCurriculumNodeInfo] = useState(null); // { name, breadcrumbs }
  const [gradeFilter, setGradeFilter] = useState(savedState?.gradeFilter ?? 0);
  const [sortBy, setSortBy] = useState(savedState?.sortBy ?? 'newest');
  const [searchQuery, setSearchQuery] = useState(savedState?.searchQuery ?? '');
  const [page, setPage] = useState(savedState?.page ?? 1);

  // null = inherit from the UI locale / profile country. A non-null value is an
  // EXPLICIT sidebar choice ('' = that axis switched off, 'all' = every
  // language). Storing "inherit" as null rather than snapshotting the context
  // avoids an init race: the jurisdiction arrives async, so a snapshot would
  // freeze its pre-load value.
  const [langFilter, setLangFilter] = useState(savedState?.langFilter ?? null);
  const [jurFilter, setJurFilter] = useState(savedState?.jurFilter ?? null);
  const [curriculumFilter, setCurriculumFilter] = useState(savedState?.curriculumFilter ?? '');
  const [facets, setFacets] = useState({ languages: [], jurisdictions: [], curricula: [] });

  // The catalog is English/US-only today, so an inherited default must be proven
  // stockable before it is applied — otherwise every pt-BR / es-419 / fr user
  // opens Printables to an empty grid. No facets (not loaded yet, or the worker
  // is unreachable) means no proof, so both axes stay unfiltered.
  const langOK = (code) => facets.languages.some(l => l.code === code && l.count > 0);
  const jurOK = (code) => facets.jurisdictions.some(j => j.code === code && j.count > 0);

  // The facets request is scoped by JURISDICTION ONLY, and by the raw external
  // inputs (explicit pick, else the profile country) — never by the derived
  // values below. That is load-bearing: deriving the scope from
  // effectiveLanguage/effectiveJurisdiction creates a feedback loop with no
  // fixed point whenever a country's stock is not in the user's UI language.
  // Argentina + an English UI oscillated forever (2026-08-23): unscoped facets
  // offer both `en` and ARG, but facets?language=en&jurisdiction=ARG drop each
  // other — ARG has no English posters — so the defaults flip on every response
  // and the grid refetches endlessly.
  // Scoping by jurisdiction alone is a fixed point because the worker's
  // `jurisdictions` arm ignores ?jurisdiction (it honours ?language only), so
  // jurOK stays stable, while `languages` honours ?jurisdiction and therefore
  // reports what actually exists IN that country.
  const facetScope = jurFilter ?? schoolStateCode ?? '';

  // A country that HAS an official curriculum tree is worth selecting even with
  // zero stock — the tree makes the emptiness legible, and shows the teacher
  // their own ministry's document structure. So stock alone no longer gates the
  // inherited default. `curriculumCapable` comes from a parameterless fetch and
  // is constant for the session, so swapping jurOK → jurUsable here keeps
  // effectiveJurisdiction a fixed point and cannot reopen the 2026-08-23 loop.
  const curriculumCapable = useMemo(
    () => new Set(curriculumJurisdictions.map(j => j.code)),
    [curriculumJurisdictions],
  );
  const jurUsable = (code) => jurOK(code) || curriculumCapable.has(code);

  const effectiveJurisdiction = jurFilter ?? ((schoolStateCode && jurUsable(schoolStateCode)) ? schoolStateCode : '');

  // Which tree renders. Every US state, every territory that is not in the
  // capable list, and the no-jurisdiction case keep today's CategoryTree
  // byte-for-byte.
  const useCurriculumTree = !!effectiveJurisdiction && curriculumCapable.has(effectiveJurisdiction);

  // Only a node belonging to the tree that is actually on screen may narrow the
  // grid. A node restored from sessionStorage while the curriculum list is
  // still in flight — or when the worker is unreachable and CategoryTree
  // renders instead — must never silently filter the catalog.
  const activeCurriculumNodeId = useCurriculumTree ? selectedCurriculumNodeId : null;
  // THE COUNTRY'S CURRICULUM LANGUAGE — offered, no longer the default.
  //
  // Every one of the 37 offered jurisdictions publishes in exactly ONE language
  // (es 19, en 15, fr 2, pt 1 — measured 2026-08-24; VI is excluded by the
  // offer gate in be_printables/src/routes/curriculum.ts for having 0 labels).
  // From 2026-08-24 to 2026-09-08 that language was the DEFAULT once a country
  // was picked (Chile + English UI showed Chile's 41 Spanish posters, not the 2
  // stray English ones). The owner reversed that on 2026-09-08 — see the
  // precedence note below — so today it is the FIRST CHIP, one click away,
  // and the UI language decides the default.
  //
  // `curriculumJurisdictions` is a parameterless, session-constant fetch, so
  // reading it here cannot reopen the 2026-08-23 facets feedback loop.
  const jurisdictionLanguage = useMemo(() => {
    if (!effectiveJurisdiction) return null;
    const j = curriculumJurisdictions.find(x => x.code === effectiveJurisdiction);
    // Exactly one language per jurisdiction — [0] is the whole story, not a pick.
    return j?.languages?.[0] ?? null;
  }, [curriculumJurisdictions, effectiveJurisdiction]);

  // Precedence: explicit sidebar chip > the UI language. Nothing else.
  //
  // Owner spec 2026-09-08: "if I change language it remains on that curriculum
  // but is now in that language, and ONLY shows posters in that language."
  // So the inherited default is the UI language, strictly — no fallback to the
  // country's curriculum language and no facet guard. A country + language
  // pair with no stock (Bahamas + Português today) opens on the empty state,
  // which names the language and offers "Show English printables" / "Show all
  // regions" one click away. That is the owner's chosen trade: a grid that
  // never shows a language the teacher did not pick, over a grid that is
  // never empty. (Supersedes both "facet-guarded defaults" of 2026-08-19 and
  // "language follows the country" of 2026-08-24 for the DEFAULT; the country's
  // language is still offered first among the chips below.)
  const inheritedLanguage = uiLanguageFamily;
  const effectiveLanguage = langFilter ?? inheritedLanguage;
  const catalogFilters = useMemo(
    () => ({ language: effectiveLanguage, jurisdiction: effectiveJurisdiction, curriculum: curriculumFilter }),
    [effectiveLanguage, effectiveJurisdiction, curriculumFilter],
  );
  // The curriculum tree additionally carries the UI language so the worker can
  // return displayNames in it (migration 0038). Kept OUT of catalogFilters:
  // that object drives the poster fetch and the facets, and the UI language
  // must never narrow either.
  const curriculumFilters = useMemo(
    () => ({ ...catalogFilters, ui: uiLanguageFamily }),
    [catalogFilters, uiLanguageFamily],
  );

  const [selectedPosterId, setSelectedPosterId] = useState(null);
  const [totalPosters, setTotalPosters] = useState(0);
  const [selectedCategoryName, setSelectedCategoryName] = useState(null);
  // True when selectedCategoryName is already the worker's UI-language
  // displayName — the breadcrumb must then render it verbatim, not run it
  // through the categoryNames lookup a second time.
  const [selectedCategoryRaw, setSelectedCategoryRaw] = useState(false);

  const searchTimeoutRef = useRef(null);
  const gridAreaRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);

  // Measure grid area and calculate how many cards fit
  useEffect(() => {
    const el = gridAreaRef.current;
    if (!el) return;

    const MIN_CARD_WIDTH = 250;
    const GAP = 20;
    const CARD_BOTTOM = 118; // Brightivity card: bold 2-line title + tag row

    const compute = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width <= 0 || height <= 0) return;

      const cols = Math.max(1, Math.floor((width + GAP) / (MIN_CARD_WIDTH + GAP)));
      const cardWidth = (width - (cols - 1) * GAP) / cols;
      const cardHeight = cardWidth * 1.5 + CARD_BOTTOM;
      const rows = Math.max(1, Math.floor((height + GAP) / (cardHeight + GAP)));
      setVisibleCount(cols * rows);
    };

    const ro = new ResizeObserver(compute);
    ro.observe(el);
    compute();
    return () => ro.disconnect();
  }, [showLanding]);

  // Fetch posters when filters change (skip when on landing page)
  useEffect(() => {
    if (!visibleCount || showLanding) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    const delay = searchQuery ? 300 : 0;
    searchTimeoutRef.current = setTimeout(() => {
      const grade = GRADE_BANDS[gradeFilter];
      if (searchQuery.length >= 2) {
        searchPosters({ query: searchQuery, gradeMin: grade.min, gradeMax: grade.max, page, limit: visibleCount, curriculumNode: activeCurriculumNodeId, ...catalogFilters })
          // IIG Phase 5 — search-demand logging (fire-and-forget; worker dedupes
          // 10 min + skips page>1; a logging failure never touches search UX).
          .then((data) => {
            if (data) logSearch(searchQuery, data.total ?? 0, { gradeMin: grade.min, gradeMax: grade.max, page });
          })
          .catch(() => {});
      } else {
        fetchPosters({ categoryId: selectedCategoryId, curriculumNode: activeCurriculumNodeId, gradeMin: grade.min, gradeMax: grade.max, sort: sortBy, page, limit: visibleCount, ...catalogFilters });
      }
    }, delay);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [selectedCategoryId, activeCurriculumNodeId, gradeFilter, sortBy, searchQuery, page, visibleCount, showLanding, fetchPosters, searchPosters, logSearch, catalogFilters]);

  // Reset page on filter change. Every catalog-filter change — including the
  // empty state's one-click switches — funnels through catalogFilters, so this
  // is the only pagination reset needed.
  useEffect(() => { setPage(1); }, [selectedCategoryId, activeCurriculumNodeId, gradeFilter, sortBy, searchQuery, catalogFilters]);

  // Switching country invalidates a selected curriculum node outright: node ids
  // are jurisdiction-scoped, so the old one would resolve to nothing.
  //
  // The '' → 'BRA' transition is NOT a switch: effectiveJurisdiction starts
  // empty on every mount and only resolves once the facet + curriculum lists
  // land, so clearing on it would wipe the node restored from sessionStorage on
  // every remount. Only a change away from an already-resolved country counts.
  const lastJurisdictionRef = useRef(effectiveJurisdiction);
  useEffect(() => {
    const prev = lastJurisdictionRef.current;
    lastJurisdictionRef.current = effectiveJurisdiction;
    if (prev && prev !== effectiveJurisdiction) setSelectedCurriculumNodeId(null);
  }, [effectiveJurisdiction]);

  // Headline count — scoped to the active filter, so the landing hero stat and
  // the tree's "All Printables" badge agree with the grid.
  useEffect(() => {
    fetchRootCategories(catalogFilters).then(data => setTotalPosters(data.totalPosters || 0)).catch(() => {});
  }, [fetchRootCategories, catalogFilters]);

  // Facets drive the sidebar option lists, their counts, and the empty-state
  // diagnosis. Keyed on the two axes the worker narrows by (never curriculum).
  useEffect(() => {
    let cancelled = false;
    fetchCatalogFacets({ jurisdiction: facetScope })
      .then(f => { if (!cancelled) setFacets(f); });
    return () => { cancelled = true; };
  }, [fetchCatalogFacets, facetScope]);

  // Persist view state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('printables_view_state', JSON.stringify({
      selectedCategoryId, selectedCurriculumNodeId, gradeFilter, sortBy, searchQuery, page, showLanding,
      langFilter, jurFilter, curriculumFilter,
    }));
  }, [selectedCategoryId, selectedCurriculumNodeId, gradeFilter, sortBy, searchQuery, page, showLanding, langFilter, jurFilter, curriculumFilter]);

  // Fetch category name for breadcrumb
  useEffect(() => {
    if (selectedCategoryId) {
      fetchCategory(selectedCategoryId, { ui: uiLanguageFamily })
        .then(data => {
          setSelectedCategoryName(data?.displayName || data?.name || null);
          setSelectedCategoryRaw(!!data?.displayName);
        })
        .catch(() => { setSelectedCategoryName(null); setSelectedCategoryRaw(false); });
    } else {
      setSelectedCategoryName(null);
      setSelectedCategoryRaw(false);
    }
  }, [selectedCategoryId, fetchCategory, uiLanguageFamily]);

  // Curriculum node name + ancestor chain, for the breadcrumb and the
  // node-specific empty copy. A 404 means a re-ingest moved the node under us:
  // clear the selection and fall back to the subject roots, never an error.
  useEffect(() => {
    if (!activeCurriculumNodeId) { setCurriculumNodeInfo(null); return; }
    let cancelled = false;
    fetchCurriculumNode(activeCurriculumNodeId, { ui: uiLanguageFamily })
      .then(data => { if (!cancelled) setCurriculumNodeInfo(data ?? null); })
      .catch(err => {
        if (cancelled) return;
        setCurriculumNodeInfo(null);
        if (err?.status === 404) setSelectedCurriculumNodeId(null);
      });
    return () => { cancelled = true; };
  }, [activeCurriculumNodeId, fetchCurriculumNode, uiLanguageFamily]);

  // Search exits landing automatically
  useEffect(() => {
    if (searchQuery.length >= 2 && showLanding) setShowLanding(false);
  }, [searchQuery, showLanding]);

  // IIG Phase 4 — fetch standard alignment for the visible posters (batch, ≤48).
  useEffect(() => {
    if (!posters.length) { setAlignments({}); return; }
    const ids = posters.slice(0, 48).map(p => p.id).filter(Boolean);
    if (!ids.length) { setAlignments({}); return; }
    let cancelled = false;
    const headers = {};
    if (user?.token) headers['Authorization'] = `Bearer ${user.token}`;
    const qs = new URLSearchParams({ ids: ids.join(','), state: schoolStateCode || '' });
    fetch(`${PRINTABLES_API}/posters/alignment?${qs.toString()}`, { headers })
      .then(r => r.json())
      .then(j => { if (!cancelled && j?.success) setAlignments(j.data?.alignments || {}); })
      .catch(() => { if (!cancelled) setAlignments({}); });
    return () => { cancelled = true; };
  }, [posters, schoolStateCode, user?.token]);

  // Mutual exclusion lives ONLY in the client: each select handler clears the
  // other axis, so exactly one of category_id / curriculum_node is ever sent.
  const handleCategorySelect = useCallback((id) => {
    setSelectedCurriculumNodeId(null);
    setSelectedCategoryId(id);
    setSearchQuery('');
    setShowLanding(false);
  }, []);

  const handleCurriculumSelect = useCallback((id) => {
    setSelectedCategoryId(null);
    setSelectedCurriculumNodeId(id);
    setSearchQuery('');
    setShowLanding(false);
  }, []);

  const handleViewAll = useCallback(() => {
    setSelectedCategoryId(null);
    setSelectedCurriculumNodeId(null);
    setSearchQuery('');
    setShowLanding(false);
  }, []);

  const handleBackToLanding = useCallback(() => {
    setShowLanding(true);
  }, []);

  // Web print handoff: URL only. R2 CORS-blocks a browser fetch() of the asset
  // (an <img src> is fine), so WebPrintPreview's mount-time consumer takes the
  // URL form and renders it directly.
  const handlePrint = useCallback((poster) => {
    const imageUrl = poster.previewUrl || poster.thumbnailUrl;
    if (!imageUrl) return;
    // Brightivity: the Print page copies the full-size file server-side (same handoff as Library).
    handToPrint([{ remote: true, posterId: poster.id || null, name: poster.title || 'Printable', previewUrl: poster.previewUrl || poster.thumbnailUrl, sourceUrl: poster.fullResUrl || imageUrl }]);
    navigate('/print');
  }, [navigate]);

  const handleBuy = useCallback(async (poster) => {
    if (!isAuthenticated) {
      showError(t('purchase.signInRequired'));
      return;
    }
    if (purchaseInFlight) return;

    // Short-circuit client-side if pricing is loaded and balance is too low
    // (skipped entirely for free-tier users — nothing is debited)
    if (purchaseCost != null && purchaseCost > 0 && !canAfford(PRINTABLES_FEATURE_KEY)) {
      setShowInsufficientModal(true);
      return;
    }

    setPurchaseInFlight(true);
    try {
      // An explicit buy / re-add overrides any earlier "deleted from library"
      // suppression, so the entitlement restore can manage this printable again.
      unsuppressPrintableRestore(user?.userId, poster.id);
      const result = await purchaseAndSave({
        poster,
        purchasePrintable,
        saveDesign,
      });

      if (result.status === 'insufficient') {
        setShowInsufficientModal(true);
        return;
      }

      if (result.status === 'error') {
        const errKey = PURCHASE_ERROR_KEYS[result.errorCode];
        showError(errKey ? t(errKey, { detail: result.errorDetail }) : (result.error || t('purchase.errors.purchaseFailed')));
        if (result.purchased) {
          // Entitlement recorded server-side even though local save failed — mark owned
          // so the UI updates; sync-on-signin will create the local row next time.
          setLocallyOwnedIds(prev => new Set(prev).add(poster.id));
        }
        return;
      }

      // Success
      setLocallyOwnedIds(prev => new Set(prev).add(poster.id));
      refreshBalance();
      setSuccessPrintable({
        printable: result.printable,
        alreadyOwned: result.alreadyOwned,
        newBalance: result.newBalance,
      });
    } finally {
      setPurchaseInFlight(false);
    }
  }, [isAuthenticated, user?.userId, purchaseInFlight, purchaseCost, canAfford, purchasePrintable, saveDesign, refreshBalance, showError, t]);

  // Print, its own button next to Save (owner 2026-10-26). The catalog only lists previews; the full-size file comes from
  // the purchase endpoint, which is free for tiers A/B and master admins and never charges a printable twice. So Print
  // asks it first (no library save — that is the Save button), then hands the full-size file to the Print page.
  const handlePrintNow = useCallback(async (poster) => {
    if (!isAuthenticated) { showError(t('purchase.signInRequired')); return; }
    if (purchaseInFlight) return;
    const owned = poster.owned === true || locallyOwnedIds.has(poster.id);
    if (!owned && purchaseCost != null && purchaseCost > 0 && !canAfford(PRINTABLES_FEATURE_KEY)) { setShowInsufficientModal(true); return; }
    setPurchaseInFlight(true);
    try {
      const r = await purchasePrintable(poster.id, `printables_${poster.id}_${globalThis.crypto?.randomUUID?.() ?? Date.now()}`);
      const p = r && r.printable;
      if (!p || !p.fullResUrl) { showError(t('purchase.errors.purchaseFailed')); return; }
      setLocallyOwnedIds((prev) => new Set(prev).add(poster.id));
      if (!owned) refreshBalance();
      handlePrint({ ...poster, ...p, id: poster.id });
    } catch (err) {
      if (err && err.status === 402) setShowInsufficientModal(true);
      else showError((err && err.message) || t('purchase.errors.purchaseFailed'));
    } finally {
      setPurchaseInFlight(false);
    }
  }, [isAuthenticated, purchaseInFlight, locallyOwnedIds, purchaseCost, canAfford, purchasePrintable, refreshBalance, handlePrint, showError, t]);

  const handleShowInLibrary = useCallback(() => {
    setSuccessPrintable(null);
    setSelectedPosterId(null);
    navigate('/library');
  }, [navigate]);

  const handlePrintNowFromSuccess = useCallback(() => {
    const p = successPrintable?.printable;
    setSuccessPrintable(null);
    if (p) handlePrint(p);
  }, [successPrintable, handlePrint]);

  // Decorate posters with the locally-owned optimistic flag. Server-returned
  // `owned` still takes precedence (it's the authoritative value on next refetch).
  const decoratedPosters = locallyOwnedIds.size === 0
    ? posters
    : posters.map(p => (p.owned || !locallyOwnedIds.has(p.id)) ? p : { ...p, owned: true });

  // Region options prefer the facet list (it carries per-region counts); the
  // shared /api/states list is the fallback while facets are in flight or the
  // worker is down, and groupJurisdictions falls back again to the offline US
  // set — so the picker always renders something.
  const regionSource = facets.jurisdictions.length ? facets.jurisdictions : jurisdictions;
  const { us: usRegions, countries: countryRegions } = groupJurisdictions(regionSource);
  const regionName = (code) =>
    regionSource.find(j => j.code === code)?.name ?? code;

  // Chips, in precedence order: the selected country's curriculum language first
  // (it is the default, so it must be visible and de-selectable), then the UI
  // locale, then English as the always-available escape, then All.
  // An English user with no country sees two (English | All); a French teacher in
  // Brazil sees four (Português | Français | English | All).
  const langChips = [...new Set(
    [jurisdictionLanguage, uiLanguageFamily, 'en', LANGUAGE_ALL].filter(Boolean),
  )];

  // Empty-state diagnosis comes from the facets, never from a hardcoded
  // "the catalog is English-only" claim — the installer ships forever, so the
  // copy has to self-heal the moment localized stock lands. No facets ⇒ no
  // flags ⇒ today's generic copy.
  const langBlocked = effectiveLanguage !== LANGUAGE_ALL
    && facets.languages.length > 0
    && !langOK(effectiveLanguage);
  const jurBlocked = !!effectiveJurisdiction
    && facets.jurisdictions.length > 0
    && !jurOK(effectiveJurisdiction);
  const englishOnly = facets.languages.length === 1 && facets.languages[0]?.code === 'en';

  // Curriculum-node identity for the breadcrumb and the empty copy. Same
  // lookup the tree row uses (owner spec 2026-09-08: the curriculum follows the
  // UI language): an English-curriculum subject like LCA "Mathematics" reads
  // "Matemáticas" here exactly as it does in the sidebar; ministry wording with
  // no catalog key stays verbatim. The breadcrumb gets `raw` so it does not
  // run the lookup a second time on an already-translated string.
  const curriculumNodeName = activeCurriculumNodeId && curriculumNodeInfo?.name
    ? (curriculumNodeInfo.displayName || categoryLabel(t, curriculumNodeInfo.name))
    : null;
  // The document the node sits in — "BNCC — Ensino Fundamental — Matemática" —
  // so an empty node still tells the teacher exactly where they are.
  const curriculumNodeContext = (curriculumNodeInfo?.breadcrumbs ?? [])
    .filter(b => b.type === 'document' && b.name)
    .map(b => b.displayName || b.name)[0] ?? null;
  // A node reading zero is the NORMAL case (live stock is 1–10 posters per
  // jurisdiction against 1,000+ standards), so it gets its own deliberate copy
  // instead of the generic "adjust your filters" — which would wrongly suggest
  // the teacher did something wrong.
  const curriculumNodeEmpty = !!curriculumNodeName && !searchQuery;

  return (
    <div className="bv-pr">
      {/* Sidebar — handoff 03-Printables.png: one card with the filters and the browse tree */}
      <div data-tour="printables-filters" className="bv-pr-side">
        {/* Region — broadest scope first */}
        <div className="bv-pr-group">
          <div className="bv-pr-label">
            {t('filters.region')}
          </div>
          <select
            value={effectiveJurisdiction}
            onChange={e => setJurFilter(e.target.value)}
            className="bv-select bv-pr-select"
          >
            <option value="">{t('filters.allRegions')}</option>
            {countryRegions.length > 0 && (
              <optgroup label={t('home:statePicker.groupCountries')}>
                {countryRegions.map(s => (
                  <option key={s.code} value={s.code}>
                    {s.name}{s.count != null ? ` (${s.count})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label={t('home:statePicker.groupUnitedStates')}>
              {usRegions.map(s => (
                <option key={s.code} value={s.code}>
                  {s.name}{s.count != null ? ` (${s.count})` : ''}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Language — chip row, native names, never translated */}
        <div className="bv-pr-group">
          <div className="bv-pr-label">
            {t('filters.language')}
          </div>
          <div className="bv-pr-chips">
            {langChips.map(chip => (
              <button
                key={chip}
                onClick={() => setLangFilter(chip)}
                type="button" aria-pressed={effectiveLanguage === chip} className={`bv-pr-chip${effectiveLanguage === chip ? ' is-on' : ''}`}
              >
                {chip === LANGUAGE_ALL ? t('filters.languageAll') : LANGUAGE_FAMILY_LABELS[chip]}
              </button>
            ))}
          </div>
        </div>

        {/* Curriculum — hidden when the region offers only one; labels come from
            the worker so they can never drift from the admin Gallery's. */}
        {facets.curricula.length > 1 && (
          <div className="bv-pr-group">
            <div className="bv-pr-label">
              {t('filters.curriculum')}
            </div>
            <select
              value={curriculumFilter}
              onChange={e => setCurriculumFilter(e.target.value)}
              className="bv-select bv-pr-select"
            >
              <option value="">{t('filters.allCurricula')}</option>
              {facets.curricula.map(c => (
                <option key={c.key} value={c.key}>
                  {c.label} ({c.count})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Grade filter */}
        <div className="bv-pr-group">
          <div className="bv-pr-label">
            {t('filters.gradeLevel')}
          </div>
          <div className="bv-pr-chips">
            {GRADE_BANDS.map((band, i) => (
              <button
                key={band.key}
                onClick={() => setGradeFilter(i)}
                type="button" aria-pressed={gradeFilter === i} className={`bv-pr-chip${gradeFilter === i ? ' is-on' : ''}`}
              >
                {t(`filters.grades.${band.key}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Browse tree — exactly ONE of the two renders. A jurisdiction that
            publishes its own official curriculum gets that curriculum; every US
            state, every territory that is not curriculum-capable, and the
            no-jurisdiction case keep today's CategoryTree byte-for-byte. */}
        {useCurriculumTree ? (
          <CurriculumTree
            jurisdiction={effectiveJurisdiction}
            filters={curriculumFilters}
            selectedNodeId={showLanding ? '__landing__' : selectedCurriculumNodeId}
            onNodeSelect={handleCurriculumSelect}
            onViewAll={handleViewAll}
            totalPosters={totalPosters}
            fetchCurriculumTree={fetchCurriculumTree}
            fetchCurriculumChildren={fetchCurriculumChildren}
          />
        ) : (
          <CategoryTree
            fetchRootCategories={fetchRootCategories}
            fetchCategoryChildren={fetchCategoryChildren}
            selectedCategoryId={showLanding ? '__landing__' : selectedCategoryId}
            onCategorySelect={handleCategorySelect}
            onViewAll={handleViewAll}
            totalPosters={totalPosters}
            filters={curriculumFilters}
          />
        )}
      </div>

      {/* Main content */}
      <div className="bv-pr-main">
        <div className="bv-pr-top">
          <label className="bv-pr-search">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input
              type="text"
              aria-label={t('search.placeholder')}
              placeholder={totalPosters ? `Search ${formatNumber(totalPosters)} printables` : t('search.placeholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </label>
          <div className="bv-field bv-field--select bv-pr-sort">
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label={t('filters.sortBy')} className="bv-select">
              <option value="newest">Sort: {t('sort.newest')}</option>
              <option value="popular">Sort: {t('sort.popular')}</option>
              <option value="title">Sort: {t('sort.titleAz')}</option>
              <option value="oldest">Sort: {t('sort.oldest')}</option>
            </select>
            <svg className="bv-field__chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </div>
        </div>
        {showLanding ? (
          <PrintablesLanding
            totalPosters={totalPosters}
            onCategorySelect={handleCategorySelect}
            onBrowseAll={handleViewAll}
          />
        ) : (
          <>
            {/* Breadcrumb */}
            {/* Header with integrated breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
              <PrintablesBreadcrumb
                categoryName={curriculumNodeName ?? selectedCategoryName}
                raw={!!curriculumNodeName || selectedCategoryRaw}
                onBackToLanding={handleBackToLanding}
              />
              <span style={{ fontSize: '1rem', fontWeight: 800, color: 'rgba(27,37,80,0.6)', flexShrink: 0 }}>
                {loading ? '...' : t('results.posters', { count: total, formatted: formatNumber(total) })}
              </span>
            </div>

            {/* Error */}
            {error && (
              <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(255,81,81,0.1)', border: '1px solid rgba(255,81,81,0.2)', color: 'rgba(255,81,81,0.9)', fontSize: '0.82rem', marginBottom: 16, flexShrink: 0 }}>
                {error}
              </div>
            )}

            {/* Grid area — measured by ResizeObserver to calculate how many cards fit */}
            <div ref={gridAreaRef} style={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 0 }}>
              {/* Loading skeleton (initial load only) */}
              {loading && posters.length === 0 && visibleCount > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 20 }}>
                  {Array.from({ length: visibleCount }).map((_, i) => (
                    <div key={i} style={{ borderRadius: 16, overflow: 'hidden', background: '#FFFFFF', border: '1px solid rgba(27,37,80,0.057)' }}>
                      <div style={{ aspectRatio: '2/3', background: '#FFFFFF', animation: 'pulse 1.5s ease-in-out infinite' }} />
                      <div style={{ padding: 12 }}>
                        <div style={{ height: 14, background: '#FFFFFF', borderRadius: 6, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <div style={{ height: 10, width: '60%', background: '#FFFFFF', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!loading && posters.length === 0 && !error && (
                <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto 16px', opacity: 0.3 }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#fff" strokeWidth="1.5" />
                    <path d="M3 16l5-5 4 4 3-3 6 6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <h3 style={{ color: 'rgba(27,37,80,0.475)', margin: '0 0 8px', fontWeight: 600 }}>
                    {curriculumNodeEmpty
                      ? t('empty.noneForNode', { node: curriculumNodeName })
                      : langBlocked
                        ? t('empty.noneInLanguage', { language: LANGUAGE_FAMILY_LABELS[effectiveLanguage] })
                        : jurBlocked
                          ? t('empty.noneInRegion', { region: regionName(effectiveJurisdiction) })
                          : t('empty.title')}
                  </h3>
                  <p style={{ color: 'rgba(27,37,80,0.285)', fontSize: '0.85rem' }}>
                    {curriculumNodeEmpty
                      ? (curriculumNodeContext
                          ? t('empty.nodeContext', { context: curriculumNodeContext })
                          : t('empty.nodeNoContext'))
                      : (langBlocked || jurBlocked) && englishOnly
                        ? t('empty.catalogEnglishOnly')
                        : searchQuery ? t('empty.noResults', { query: searchQuery }) : t('empty.adjustFilters')}
                  </p>
                  {/* No "Generate one" call to action on an empty curriculum
                      node (removed 2026-09-04, owner request). Browsing a
                      ministry's curriculum is a browse task: >99% of nodes are
                      legitimately empty, so the button fired on almost every
                      node and pushed the teacher out of Printables into the
                      generator. The explanatory copy above is the useful half
                      and stays. */}
                  {!curriculumNodeEmpty && (langBlocked || jurBlocked) && (
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
                      {langBlocked && (
                        <button
                          onClick={() => { setLangFilter('en'); if (jurBlocked) setJurFilter(''); }}
                          style={{
                            padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                            fontSize: '0.8rem', fontWeight: 600,
                            background: 'rgba(26,176,242,0.25)', color: '#1B2550',
                          }}
                        >
                          {t('empty.switchToEnglish')}
                        </button>
                      )}
                      {jurBlocked && (
                        <button
                          onClick={() => setJurFilter('')}
                          style={{
                            padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                            fontSize: '0.8rem', fontWeight: 600,
                            background: '#FFFFFF', color: 'rgba(27,37,80,0.665)',
                          }}
                        >
                          {t('empty.clearRegion')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Poster grid */}
              {posters.length > 0 && (
                <>
                  {/* Loading overlay for page transitions */}
                  {loading && (
                    <div style={{
                      position: 'absolute', inset: 0, zIndex: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#FFFFFF', borderRadius: 16, backdropFilter: 'blur(1px)',
                      animation: 'fadeIn 0.15s ease',
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 20px', borderRadius: 12,
                        background: 'rgba(30,30,40,0.9)', border: '1px solid rgba(27,37,80,0.095)',
                        boxShadow: '0 8px 32px rgba(27,37,80,0.14)',
                      }}>
                        <div style={{
                          width: 16, height: 16, borderRadius: '50%',
                          border: '2px solid rgba(27,37,80,0.142)',
                          borderTopColor: '#1AB0F2',
                          animation: 'spin 0.6s linear infinite',
                        }} />
                        <span style={{ fontSize: '0.8rem', color: 'rgba(27,37,80,0.57)', fontWeight: 500 }}>
                          {t('loading.page', { page })}
                        </span>
                      </div>
                    </div>
                  )}
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 20,
                    opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease',
                    pointerEvents: loading ? 'none' : 'auto',
                  }}>
                    {decoratedPosters.map(poster => (
                      <PrintableCard
                        key={poster.id}
                        poster={poster}
                        onClick={setSelectedPosterId}
                        onPrint={isAuthenticated ? handlePrintNow : undefined}
                        onBuy={isAuthenticated ? handleBuy : undefined}
                        purchaseCost={purchaseCost}
                        alignment={alignments[poster.id] ?? null}
                        viewerStateCode={schoolStateCode}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Pagination — pinned at bottom */}
            {totalPages > 1 && (
              <div className="bv-pager bv-pr-pager">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="bv-btn"
                >
                  {t('pagination.previous')}
                </button>
                <span className="bv-pager__label">
                  {t('pagination.pageOf', { page, totalPages })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="bv-btn bv-btn--blue"
                >
                  {t('pagination.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail modal */}
      {selectedPosterId && (
        <PrintableDetail
          posterId={selectedPosterId}
          fetchPoster={async (id) => {
            const p = await fetchPoster(id);
            // Respect optimistic local ownership so the modal immediately reflects a
            // just-completed purchase without a backend refetch.
            if (p && !p.owned && locallyOwnedIds.has(id)) return { ...p, owned: true };
            return p;
          }}
          fetchSimilarPosters={fetchSimilarPosters}
          onClose={() => setSelectedPosterId(null)}
          onPosterClick={setSelectedPosterId}
          onPrint={isAuthenticated ? handlePrintNow : undefined}
          onBuy={isAuthenticated ? handleBuy : undefined}
          purchaseCost={purchaseCost}
        />
      )}

      {/* Purchase success modal */}
      {successPrintable && (
        <PurchaseSuccessModal
          printable={successPrintable.printable}
          alreadyOwned={successPrintable.alreadyOwned}
          newBalance={successPrintable.newBalance}
          onShowInLibrary={handleShowInLibrary}
          onPrintNow={handlePrintNowFromSuccess}
          onClose={() => setSuccessPrintable(null)}
        />
      )}

      {/* Insufficient credits → top-up flow */}
      <CreditPurchaseModal
        isOpen={showInsufficientModal}
        onClose={() => setShowInsufficientModal(false)}
      />

      {/* In-flight shim — blocks accidental double-clicks while a purchase is running */}
      {purchaseInFlight && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10050,
          background: 'rgba(27,37,80,0.14)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto',
        }}>
          <div style={{
            padding: '14px 22px', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12,
            background: 'rgba(30,30,40,0.95)', border: '1px solid rgba(27,37,80,0.095)',
            color: '#1B2550', fontSize: '0.85rem', fontWeight: 500,
          }}>
            <div style={{
              width: 16, height: 16, borderRadius: '50%',
              border: '2px solid rgba(27,37,80,0.142)', borderTopColor: '#1AB0F2',
              animation: 'spin 0.6s linear infinite',
            }} />
            {t('purchase.addingToLibrary')}
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
